import {loadTelegramSdk} from '@/appType/appType.js'

/**
 * Telegram viewport CSS vars for BottomAppBar / compact sheet math.
 *
 * Shell scroll is natural document scroll (`html.app-telegram` in app.scss) —
 * do not lock html/body to `--document-height` (causes expanded black gaps + CTA blanks).
 *
 * - Mount viewport + bindCssVars (--tg-viewport-height / stable)
 * - `--document-height` from stable settle (px) — bar pin, compact pad formula
 * - `--tma-visible-height` / `--tma-webview-height` for bar / pad
 *
 * Never call expand() — compact/full is a bot setting.
 *
 * @see https://core.telegram.org/bots/webapps#initializing-mini-apps
 * @see .cursor/skills/pwa-miniapp/SKILL.md
 */

const MOUNT_TIMEOUT_MS = 2500

/** @type {{ stable: number, height: number, applied: number, expanded: boolean|null, isStable: boolean|null, mounted: boolean, cssBound: boolean, error: string }} */
let debugInfo = {
  stable: 0,
  height: 0,
  applied: 0,
  expanded: null,
  isStable: null,
  mounted: false,
  cssBound: false,
  error: '',
}

function setWebviewHeightVar() {
  const webviewH = Math.round(window.innerHeight || 0)
  if (webviewH > 0) {
    document.documentElement.style.setProperty('--tma-webview-height', `${webviewH}px`)
  }
}

function setVisibleSheetHeight(px) {
  const height = Math.round(Number(px) || 0)
  if (height < 80) {
    return
  }
  document.documentElement.style.setProperty('--tma-visible-height', `${height}px`)
}

function applyDocumentHeight(px) {
  const height = Math.round(Number(px) || 0)
  if (height < 80) {
    return
  }

  setWebviewHeightVar()
  setVisibleSheetHeight(height)

  if (height === debugInfo.applied) {
    return
  }

  document.documentElement.style.setProperty('--document-height', `${height}px`)
  debugInfo.applied = height
}

function trackLiveHeight(px) {
  const height = Math.round(Number(px) || 0)
  if (height > 0) {
    debugInfo.height = height
    setVisibleSheetHeight(height)
    setWebviewHeightVar()
  }
}

function applyStableSettle(px) {
  const height = Math.round(Number(px) || 0)
  if (height < 80) {
    return
  }
  debugInfo.stable = height
  applyDocumentHeight(height)
}

export function getTelegramViewportDebugInfo() {
  return {...debugInfo}
}

/**
 * @returns {() => void} stop
 */
export function startTelegramViewportHeightSync() {
  let stopped = false
  /** @type {Array<() => void>} */
  const cleanups = []

  setWebviewHeightVar()
  // Until SDK binds, assume full webview is visible (expanded or unknown).
  setVisibleSheetHeight(window.innerHeight || 0)

  const run = async () => {
    const sdk = await loadTelegramSdk()
    if (!sdk || stopped) {
      return
    }

    const {
      mountViewport,
      isViewportMounted,
      isViewportMounting,
      bindViewportCssVars,
      isViewportCssVarsBound,
      viewportStableHeight,
      viewportHeight,
      isViewportExpanded,
      isViewportStable,
      on,
      off,
    } = sdk

    try {
      if (mountViewport?.isAvailable?.() && !isViewportMounted?.() && !isViewportMounting?.()) {
        await Promise.race([
          mountViewport(),
          new Promise((_, reject) => {
            setTimeout(() => reject(new Error('viewport.mount timeout')), MOUNT_TIMEOUT_MS)
          }),
        ])
      }
      debugInfo.mounted = Boolean(isViewportMounted?.())
    } catch (e) {
      debugInfo.error = String(e?.message || e)
      const tg = window.Telegram?.WebApp
      if (tg?.viewportStableHeight) {
        applyStableSettle(tg.viewportStableHeight)
      }
    }

    if (stopped) {
      return
    }

    try {
      if (bindViewportCssVars?.isAvailable?.() && !isViewportCssVarsBound?.()) {
        const unbind = bindViewportCssVars()
        if (typeof unbind === 'function') {
          cleanups.push(unbind)
        }
        debugInfo.cssBound = true
      }
    } catch (e) {
      debugInfo.error = debugInfo.error || String(e?.message || e)
    }

    const syncFromSignals = () => {
      try {
        const stable = typeof viewportStableHeight === 'function'
          ? viewportStableHeight()
          : window.Telegram?.WebApp?.viewportStableHeight
        const height = typeof viewportHeight === 'function'
          ? viewportHeight()
          : window.Telegram?.WebApp?.viewportHeight
        const expanded = typeof isViewportExpanded === 'function'
          ? isViewportExpanded()
          : window.Telegram?.WebApp?.isExpanded
        const stableFlag = typeof isViewportStable === 'function'
          ? isViewportStable()
          : null

        const live = Math.round(Number(height) || 0)
        const settled = Math.round(Number(stable) || live)

        debugInfo.height = live
        debugInfo.stable = settled
        debugInfo.expanded = expanded == null ? null : Boolean(expanded)
        debugInfo.isStable = stableFlag == null ? null : Boolean(stableFlag)

        if (stableFlag !== false) {
          applyStableSettle(settled || live)
        } else {
          trackLiveHeight(live)
        }
      } catch (e) {
        debugInfo.error = String(e?.message || e)
      }
    }

    syncFromSignals()

    if (typeof viewportHeight?.sub === 'function') {
      cleanups.push(viewportHeight.sub((current) => {
        if (!stopped) {
          trackLiveHeight(current)
        }
      }))
    }

    if (typeof viewportStableHeight?.sub === 'function') {
      cleanups.push(viewportStableHeight.sub(() => {
        if (!stopped) {
          syncFromSignals()
        }
      }))
    }

    if (typeof on === 'function') {
      const onViewportChanged = (payload) => {
        if (stopped) {
          return
        }
        const h = Math.round(Number(payload?.height) || 0)
        const stableEvent = payload?.is_state_stable ?? payload?.isStateStable
        trackLiveHeight(h)
        if (stableEvent != null) {
          debugInfo.isStable = Boolean(stableEvent)
        }
        if (stableEvent === true && h >= 80) {
          debugInfo.stable = h
          applyStableSettle(h)
        }
      }
      on('viewport_changed', onViewportChanged)
      cleanups.push(() => {
        try {
          off?.('viewport_changed', onViewportChanged)
        } catch {
          // ignore
        }
      })
    } else {
      // Legacy bridge if SDK `on` is unavailable
      const tg = window.Telegram?.WebApp
      if (tg?.onEvent) {
        const onChanged = (payload) => {
          if (stopped) {
            return
          }
          const h = Math.round(Number(payload?.height ?? tg.viewportHeight) || 0)
          const isStateStable = payload?.isStateStable ?? payload?.is_state_stable
          trackLiveHeight(h)
          if (isStateStable === true && h >= 80) {
            debugInfo.stable = h
            debugInfo.isStable = true
            applyStableSettle(h)
          } else if (isStateStable === false) {
            debugInfo.isStable = false
          }
        }
        tg.onEvent('viewportChanged', onChanged)
        cleanups.push(() => {
          try {
            tg.offEvent?.('viewportChanged', onChanged)
          } catch {
            // ignore
          }
        })
      }
    }
  }

  run()

  return () => {
    stopped = true
    while (cleanups.length) {
      try {
        cleanups.pop()?.()
      } catch {
        // ignore
      }
    }
  }
}
